import { useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { GROCERY_LIST_QUERY_KEY } from './useGroceryList'
import { groceryListService } from './grocery-list.service'
import { showToast, showUndoToast } from '@/shared/utils/toast'
import { useNetworkStatus } from '@/shared/hooks/useNetworkStatus'
import { offlineQueue } from '@/lib/offline-queue'
import { useOfflineTickStore } from './useOfflineTickStore'
import type { GroceryList, GroceryListItem } from './types'

/**
 * Returns a debounced tick handler.
 * - Optimistic update: updates cache immediately.
 * - Undo toast: 3-second window to revert via PATCH with ticked: false.
 * - Offline: queues PATCH to offline-queue; optimistic update still applies.
 */
export function useTickGroceryItem() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('grocery-list')
  const { isOnline } = useNetworkStatus()
  const { addPending } = useOfflineTickStore()

  // Per-item debounce timers — each item has its own timer
  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  // Keep a live ref of isOnline so setTimeout callbacks read current value
  const isOnlineRef = useRef(isOnline)
  isOnlineRef.current = isOnline

  // Clear pending debounce timers on unmount
  useEffect(() => {
    return () => {
      for (const timer of debounceTimers.current.values()) {
        clearTimeout(timer)
      }
      debounceTimers.current.clear()
    }
  }, [])

  const mutation = useMutation({
    mutationFn: ({ itemId, ticked }: { itemId: string; ticked: boolean }) =>
      groceryListService.tickGroceryListItem(itemId, ticked),

    onMutate: async ({ itemId, ticked }) => {
      await queryClient.cancelQueries({ queryKey: GROCERY_LIST_QUERY_KEY })
      const snapshot = queryClient.getQueryData<GroceryList>(GROCERY_LIST_QUERY_KEY)

      queryClient.setQueryData<GroceryList>(GROCERY_LIST_QUERY_KEY, (prev) => {
        if (!prev) return prev
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.id === itemId ? { ...item, ticked } : item,
          ),
        }
      })

      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(GROCERY_LIST_QUERY_KEY, context.snapshot)
      }
      showToast(t('tick.errors.tickFailed'))
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: GROCERY_LIST_QUERY_KEY })
    },
  })

  function handleTick(item: GroceryListItem) {
    // Debounce: cancel any pending call for this specific item
    const existing = debounceTimers.current.get(item.id)
    if (existing) clearTimeout(existing)

    const timer = setTimeout(() => {
      debounceTimers.current.delete(item.id)

      // Read current ticked state from cache to avoid stale closure
      const current = queryClient.getQueryData<GroceryList>(GROCERY_LIST_QUERY_KEY)
      const currentItem = current?.items.find(i => i.id === item.id)
      const nextTicked = !(currentItem?.ticked ?? item.ticked)

      if (!isOnlineRef.current) {
        // Offline path: apply optimistic update + enqueue
        queryClient.setQueryData<GroceryList>(GROCERY_LIST_QUERY_KEY, (prev) => {
          if (!prev) return prev
          return {
            ...prev,
            items: prev.items.map((i) =>
              i.id === item.id ? { ...i, ticked: nextTicked } : i,
            ),
          }
        })
        offlineQueue.enqueue({
          method: 'PATCH',
          path: `/api/v1/grocery-list/items/${item.id}/tick`,
          body: { ticked: nextTicked },
          meta: { itemId: item.id },
        })
        addPending(item.id)
      } else {
        mutation.mutate({ itemId: item.id, ticked: nextTicked })
      }

      // Undo toast — only shown when ticking (not when unticking via undo to avoid loops)
      if (nextTicked) {
        showUndoToast(
          t('tick.undoToast', { name: item.ingredientName }),
          t('tick.undoLabel'),
          () => {
            if (!isOnlineRef.current) {
              // Offline undo: enqueue the reverse and revert cache
              offlineQueue.enqueue({
                method: 'PATCH',
                path: `/api/v1/grocery-list/items/${item.id}/tick`,
                body: { ticked: false },
                meta: { itemId: item.id },
              })
              addPending(item.id)
              queryClient.setQueryData<GroceryList>(GROCERY_LIST_QUERY_KEY, (prev) => {
                if (!prev) return prev
                return {
                  ...prev,
                  items: prev.items.map((i) =>
                    i.id === item.id ? { ...i, ticked: false } : i,
                  ),
                }
              })
            } else {
              mutation.mutate({ itemId: item.id, ticked: false })
            }
          },
        )
      }
    }, 250)

    debounceTimers.current.set(item.id, timer)
  }

  return { handleTick }
}
